import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * usePortalHours · v2.8+E305
 * ──────────────────────────
 * Shared LIVE rider-portal Hours-of-Operation status. Returns the status
 * object (or null while loading):
 *   { is_open, hours_label, days_label, next_open_at, next_open_label,
 *     closed_message, tz_abbrev, now_ct, ... }
 *
 * Freshness is kept by THREE independent mechanisms so the UI flips exactly
 * when hours change:
 *   1. A 20s safety poll (backstop for drift / missed events).
 *   2. A PRECISE boundary timer — when CLOSED we schedule a re-fetch for the
 *      exact moment `next_open_at` arrives (computed from the SERVER clock via
 *      `now_ct`, so it's immune to client-clock skew). This makes the portal /
 *      support chat flip online the instant hours begin, not up to 20s later.
 *   3. The public `/api/ws/rider-hours` WebSocket (with auto-reconnect) so an
 *      admin editing hours pushes an instant update to every open client.
 */
export default function usePortalHours() {
  const [hours, setHours] = useState(null);

  useEffect(() => {
    let alive = true;
    let ws = null;
    let reconnectTimer = null;
    let boundaryTimer = null;
    let unmounting = false;

    const clearBoundary = () => {
      if (boundaryTimer) { clearTimeout(boundaryTimer); boundaryTimer = null; }
    };

    // Precise flip: when closed, re-fetch exactly at next_open_at. Delay is
    // measured from the SERVER's own clock (next_open_at − now_ct) so a wrong
    // client clock can't make us fire early/late.
    const scheduleBoundary = (data) => {
      clearBoundary();
      if (!data || data.is_open || !data.next_open_at) return;
      const target = new Date(data.next_open_at).getTime();
      const serverNow = data.now_ct ? new Date(data.now_ct).getTime() : Date.now();
      if (Number.isNaN(target) || Number.isNaN(serverNow)) return;
      const delay = (target - serverNow) + 750; // small buffer past the boundary
      if (delay <= 0) { fetchHours(); return; }
      // Re-arm at least every 6h for far-off opens (keeps long timers accurate).
      const capped = Math.min(delay, 6 * 60 * 60 * 1000);
      boundaryTimer = setTimeout(fetchHours, capped);
    };

    const applyStatus = (data) => {
      if (!alive || !data) return;
      setHours(data);
      scheduleBoundary(data);
    };

    const fetchHours = () =>
      api.get("/rider-portal/hours").then((r) => applyStatus(r.data)).catch(() => {});

    const closeSocket = (sock) => {
      if (!sock) return;
      try {
        sock.onmessage = null;
        sock.onerror = null;
        sock.onclose = null;
        if (sock.readyState === WebSocket.CONNECTING) {
          // Closing a still-connecting socket logs a noisy "closed before the
          // connection is established" warning (fires under React StrictMode's
          // dev double-mount). Defer the close until the handshake completes.
          sock.onopen = () => { try { sock.close(); } catch { /* noop */ } };
        } else {
          sock.onopen = null;
          sock.close();
        }
      } catch { /* noop */ }
    };

    const connectWS = () => {
      try {
        const wsBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
        const sock = new WebSocket(`${wsBase}/api/ws/rider-hours`);
        ws = sock;
        let opened = false;
        sock.onopen = () => { opened = true; };
        sock.onmessage = (e) => {
          try {
            const m = JSON.parse(e.data);
            if (m && m.type === "rider_portal_hours_updated") applyStatus(m);
          } catch { /* ignore malformed frame */ }
        };
        sock.onclose = () => {
          if (unmounting || !alive) return;
          // Reconnect only if the socket had actually opened — avoids a tight
          // loop in environments that can't upgrade the socket at all.
          if (opened) reconnectTimer = setTimeout(connectWS, 4000);
        };
        sock.onerror = () => { /* onclose handles teardown/reconnect */ };
      } catch { /* WS optional — poll + boundary timer still cover it */ }
    };

    fetchHours();
    const poll = setInterval(fetchHours, 20000);
    connectWS();

    return () => {
      alive = false;
      unmounting = true;
      clearInterval(poll);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      clearBoundary();
      closeSocket(ws);
    };
  }, []);

  return hours;
}
